import React, { useState, useEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { Container, Row, Col, Button } from "react-bootstrap";
import axios from "axios";
import { config } from "../config/config";
import { format } from "date-fns";

const SupportRequestDetail = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (location.state?.request) {
      setRequest(location.state.request);
      setLoading(false);
    } else {
      // Otherwise fetch the request data by ID
      fetchRequestData();
    }
  }, [id, location.state]);

  const fetchRequestData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/prompts/getChatSessionById/${id}`,
        config
      );

      if (response.data) {
        // Format the data as needed
        const requestData = response.data;
        const requestDate = new Date(requestData.createdAt);
        const formattedData = {
          id: requestData.id,
          date: requestData.createdAt,
          formattedDate: format(requestDate, 'MM/dd/yyyy'),
          formattedTime: format(requestDate, 'hh:mm a'),
          type: requestData.requestType || "N/A",
          chat: requestData.chat || []
        };
        
        setRequest(formattedData);
      }
      setLoading(false);
    } catch (err) {
      console.error("Error fetching request details:", err);
      setError("Failed to load request details. Please try again later.");
      setLoading(false);
    }
  };

  // // Sample chat data for demonstration if needed
  // const sampleChat = [
  //   {
  //     role: "user",
  //     message: "Guide me to a place of inner peace.",
  //     timestamp: "2025-04-04T05:35:24.988Z"
  //   },
  //   {
  //     role: "assistant",
  //     message: "Finding inner peace is a personal journey, and it often involves a combination of mindfulness, self-reflection, and self-care practices. Here are some steps to help guide you to a place of inner peace:\n\nCreate a Quiet Space: Find a comfortable place where you can be alone for a few minutes. It could be a corner of a room, a park bench, or anywhere that feels comfortable and safe.\n\nPractice Mindfulness or Meditation: \n   Mindfulness: Focus on your breath. Inhale deeply through your nose, hold for a moment, and exhale slowly through your mouth. Repeat this several times, allowing thoughts to come and go without judgment.\n   Meditation: Sit comfortably with your back straight. Close your eyes and focus on your breath or a specific mantra. If your mind wanders, gently bring your focus back to your breath or mantra.\n\nConnect with Nature: Spend some time outside, whether in a garden, park, or any natural setting. Take in the sights, sounds, and smells. Nature has a calming effect on the mind and spirit.\n\nRelease Negativity: Write down any worries or negative feelings on a piece of paper. Acknowledge them, then choose to let them go. You can tear the paper or burn it as a symbolic gesture of releasing those thoughts.\n\nPractice Gratitude: Take a moment to reflect on three things you are grateful for in your life. Focusing on gratitude can shift your mindset and help you cultivate a sense of peace.\n\nEngage in Gentle Movement: Consider yoga, tai chi, or even simple stretching. Movement can help release tension in the body and promote a sense of calm.\n\nLimit Distractions: Take a break from screens and technology. Set aside a specific time to disconnect from social media and emails to clear your mind.\n\nNurture Yourself: Treat yourself with kindness. Engage in activities that bring you joy, whether reading a book, taking a warm bath, or practicing a hobby you love.\n\nSet Boundaries: Learn to say no when necessary. Protect your energy by establishing boundaries with others to maintain your peace of mind.\n\nReflect and Journal: Spend a few minutes writing in a journal. Reflect on your thoughts and feelings without judgment. This can be a powerful way to process emotions and gain clarity.\n\nRemember, finding inner peace is an ongoing process. It's perfectly natural to have ups and downs along the way. Be patient with yourself and allow the process to unfold at its own pace.",
  //     timestamp: "2025-04-04T05:35:24.988Z"
  //   }
  // ];

  // Show loading state
  if (loading) {
    return (
      <Container className="mt-5">
        <div className="d-flex justify-content-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </Container>
    );
  }

  // Show error state
  if (error) {
    return (
      <Container className="mt-5">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </Container>
    );
  }

  // If no request data was found
  if (!request) {
    return (
      <Container className="mt-5">
        <div className="alert alert-warning" role="alert">
          Support request not found.
        </div>
      </Container>
    );
  }

  // Use sample chat data or request.chat depending on what's available
  const chatData = request.chat && request.chat.length > 0 ? request.chat : [];

  return (
    <Container className="mt-4 mb-5">
      {/* Header Info Section */}
      <div className="bg-white rounded shadow-sm mb-4">
        <div 
          style={{ 
            backgroundColor: "#064260", 
            color: "white", 
            padding: "15px",
            borderRadius: "10px 10px 0 0"
          }}
        >
          <h5 className="mb-0">ID: {request.id}</h5>
        </div>
        
        <div className="p-3">
          <Row className="mb-3 border-bottom pb-3">
            <Col sm={3}>
              <strong>Request Date</strong>
            </Col>
            <Col>
              {request.formattedDate}
            </Col>
          </Row>
          
          <Row>
            <Col sm={3}>
              <strong>Request Type</strong>
            </Col>
            <Col>
              {request.type}
            </Col>
          </Row>
        </div>
      </div>

      {/* Activity / Chat Section */}
      <div className="bg-white rounded shadow-sm">
        <div 
          style={{ 
            backgroundColor: "#064260", 
            color: "white", 
            padding: "15px",
            borderRadius: "10px 10px 0 0"
          }}
        >
          <h5 className="mb-0">Activity</h5>
        </div>
        
        <div>
          {chatData.map((message, index) => {
            // Convert timestamp to Date object
            const messageDate = new Date(message.timestamp);
            // Format date as MM/DD/YYYY
            const formattedDate = format(messageDate, 'M/d/yyyy');
            // Format time as h:mm a (e.g., 2:00 PM)
            const formattedTime = format(messageDate, 'h:mm a');
            
            // Determine if message is from user or AI
            const isUserMessage = message.role === 'user';
            
            return (
              <div 
                key={index} 
                className={`p-3 ${index % 2 === 0 ? 'bg-light' : ''} border-bottom`}
              >
                <Row>
                <Col xs={12} md={9}>
                  <strong>
                    {isUserMessage ? 'Anonymous User' : 'AI Generated - Numente'}
                  </strong>
                  <p className="mb-0" style={{ whiteSpace: 'pre-line' }}>
                    {message.message}
                  </p>
                </Col>
                <Col xs={12} md={3} className="d-flex justify-content-center align-items-center mt-2 mt-md-0">
                  <strong>{formattedDate}, {formattedTime}</strong>
                </Col>
              </Row>
              </div>
            );
          })}
        </div>
      </div>
    </Container>
  );
};

export default SupportRequestDetail;